<template>
  <b-card v-if="hasData" no-body class="mt-n2">
    <b-row class="mt-1 mx-1">
      <b-card class="header-card">
        <h5 class="mb-1 text-center">Details</h5>
          <div class="details-row mt-2">
              <span v-if="threatDetails.behavior">
                <b-icon variant="info" class="mr-0-5" icon="shield-exclamation"/>
                <b>Behavior:</b>
                {{ threatDetails.behavior.join(', ') }}
              </span>
              <span v-if="threatDetails.totalCount">
                <b-icon :style="{ color: iconColors.purple }" class="mr-0-5" icon="arrow-left-right"/>
                <b>Total Flows:</b>
                {{ threatDetails.totalCount }}
              </span>
              <span v-if="threatDetails.totalPackets">
                <b-icon variant="warning" class="mr-0-5" icon="collection"/>
                <b>Total Packets:</b>
                {{ threatDetails.totalPackets }}
              </span>
              <span v-if="threatDetails.queryStats">
                <b-icon variant="success" class="mr-0-5" icon="tag-fill"/>
                <b>Query Streak Sample:</b>
                {{ threatDetails.queryStats.streakSample }}
              </span>
              <span v-if="threatDetails.queryStats">
                <b-icon variant="success" class="mr-0-5" icon="arrow-repeat"></b-icon>
                <b>Query Longest Streak:</b>
                {{ threatDetails.queryStats.longestStreak }}
              </span>
              <span v-if="threatDetails.roleStats">
                <b-icon variant="warning" class="mr-0-5" icon="tag-fill"/>
                <b>Role Streak Sample:</b>
                {{ threatDetails.roleStats.streakSample }}
              </span>
              <span v-if="threatDetails.roleStats">
                <b-icon variant="warning" class="mr-0-5" icon="arrow-repeat"></b-icon>
                <b>Role Longest Streak:</b>
                {{ threatDetails.roleStats.longestStreak }}
              </span>
              <span v-if="threatDetails.tldStats">
                <b-icon :style="{ color: iconColors.purple }" class="mr-0-5" icon="tag-fill"/>
                <b>Top-Level Domain Streak Sample:</b>
                {{ threatDetails.tldStats.streakSample }}
              </span>
              <span v-if="threatDetails.tldStats">
                <b-icon :style="{ color: iconColors.purple }" class="mr-0-5" icon="arrow-repeat"></b-icon>
                <b>Top-Level Domain Longest Streak:</b>
                {{ threatDetails.tldStats.longestStreak }}
              </span>
        </div>
      </b-card>
    </b-row>
    <div v-if="hasFlowsData">
      <h5 class="mb-1 text-center"> Sample Flow Records (Up to 50)</h5>
      <b-row class="mb-0-5 mx-1" align-h="between" align-v="middle">
        <b-col lg="2" md="4" sm="12" class="mb-1 mt-0-5">
          <div class="d-inline-flex align-items-center">
            Show
            <b-form-select
              v-model="perPage"
              :options="options"
              style="width: 60px;"
              size="sm"
            ></b-form-select>
            Entries
          </div>
        </b-col>
      </b-row>
      <b-row class="mx-1">
        <b-col>
          <b-table
            id="flows-table"
            class="table-with-actions-min-height"
            responsive
            striped
            outlined
            :items="flowsData"
            :fields="tableFields"
            :per-page="perPage"
            :current-page="currentPage"
          >
          <template #cell(icons)="row">
            <div class="d-flex align-items-center">
              <b-icon
                icon="arrows-angle-expand"
                class="cursor-pointer text-primary"
                title="Show entire flow json record"
                @click="showJsonModal(row.item)"
              />
              <b-icon
                v-if="ipAttrExist"
                icon="suit-diamond-fill"
                class="cursor-pointer text-primary ml-2"
                title="Show correlated xTend traffic records"
                @click="showXtendTraffic(row.item)"
              />
            </div>
          </template>
            <template #cell(@timestamp)="row">
              {{ formatDate(row.value) }}
            </template>
          </b-table>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
            <h5
              class="mt-2 mx-1 text-right"
            >
              <span
                class="total-signatures-wrapper"
                v-b-tooltip.hover
                :title="getTotalRecordsTooltip"
              >
                Total Records: {{ paginationTotalRows }}
              </span>
            </h5>
          </b-col>
        <b-col>
          <b-pagination
            v-model="currentPage"
            :total-rows="paginationTotalRows"
            :per-page="perPage"
            align="right"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            class="my-2 mx-1"
          />
        </b-col>
      </b-row>
      <b-modal id="json-modal" title="Flow Record JSON" size="lg" scrollable hide-footer>
        <pre>{{ selectedJson }}</pre>
      </b-modal>
      <XtendTrafficCorr
        v-if="flowRow"
        :commId="flowRow.network.community_id"
        :time="flowRow['@timestamp']"
        @close="flowRow = null"
      />
    </div>
  </b-card>
  <div v-else-if="loaded">{{ $t("No Records For The Selected Point") }}</div>
  <SpinnerCmpt v-else></SpinnerCmpt>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

import SpinnerCmpt from '@/xvisor/components/SpinnerCmpt.vue';
import XtendTrafficCorr from '@/xvisor/components/security/XtendTrafficCorr.vue';
import colorPalette from '@/xvisor/constants/colorPalette';
import momentTimeFormat from '@/xvisor/constants/momentTimeFormat';

export default {
  props: {
    selectedPoint: {
      type: Object,
      required: true,
    },
  },
  components: {
    SpinnerCmpt,
    XtendTrafficCorr,
  },
  mounted() {
    this.httpGet();
  },
  data() {
    return {
      threatData: [],
      flowsData: [],
      flowRow: null,
      ipAttrExist: false,
      loaded: false,
      perPage: 10,
      currentPage: 1,
      selectedJson: '',
      iconColors: {
        purple: colorPalette.purple,
      },
      options: [
        { value: 5, text: '5' },
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
      ],
    };
  },
  watch: {
    selectedPoint() {
      this.httpGet();
    },
  },
  computed: {
    tableFields() {
      const fields = [
        { key: 'icons', label: '' },
        { key: '@timestamp', label: 'Time', sortable: true },
        { key: 'client.ip', label: 'Client IP', sortable: true },
        { key: 'server.ip', label: 'Server IP', sortable: true },
        { key: 'server.port', label: 'Server Port', sortable: true },
        { key: 'network.transport', label: 'Protocol', sortable: true },
      ];
      const { appType } = this.threatDetails || {};
      // Add specific fields based on appType
      if (appType === 'dns') {
        fields.push(
          { key: 'dns.question.name', label: 'DNS Query', sortable: true },
          { key: 'dns.response_code', label: 'Response Code', sortable: true },
        );
      } else if (appType === 'http') {
        fields.push(
          { key: 'url.domain', label: 'Domain', sortable: true },
          { key: 'server.packets', label: 'Server Packets', sortable: true },
        );
      } else {
        fields.push(
          { key: 'server.bytes', label: 'Server Bytes', sortable: true },
          { key: 'server.packets', label: 'Server Packets', sortable: true },
        );
      }
      return fields;
    },
    paginationTotalRows() {
      return this.flowsData.length;
    },
    ip() {
      return this.selectedPoint.ip;
    },
    serviceType() {
      return this.selectedPoint.serviceType;
    },
    role() {
      return this.selectedPoint.role;
    },
    scope() {
      return this.selectedPoint.scope;
    },
    threatUrl() {
      return `/security/${this.ip}/drilldown.json`;
    },
    threatParams() {
      return {
        params: {
          start: this.selectedPoint.start,
          end: this.selectedPoint.end,
          service: this.serviceType,
          role: this.role,
          scope: this.scope,
        },
      };
    },
    flowsUrl() {
      return `/security/${this.ip}/flows.json`;
    },
    flowsParams() {
      return {
        params: {
          start: this.selectedPoint.start,
          end: this.selectedPoint.end,
          service: this.serviceType,
          role: this.role,
        },
      };
    },
    ipAttrUrl() {
      return `/ip/${this.ip}/attrs.json`;
    },
    ipAttrParams() {
      return {
        params: {
          start: this.selectedPoint.start,
          end: this.selectedPoint.end,
        },
      };
    },
    hasData() {
      return this.loaded && this.threatData && this.threatData.length > 0;
    },
    hasFlowsData() {
      return this.loaded && this.flowsData && this.flowsData.length > 0;
    },
    threatDetails() {
      return this.threatData[0];
    },
  },
  methods: {
    httpGet() {
      this.loaded = false;
      axios
        .all([
          this.$http.get(this.threatUrl, this.threatParams),
          this.$http.get(this.flowsUrl, this.flowsParams),
          this.$http.get(this.ipAttrUrl, this.ipAttrParams),
        ])
        .then(axios.spread((threatRes, flowsRes, ipAttrRes) => {
          this.threatData = threatRes.data;
          this.flowsData = flowsRes.data;
          this.ipAttrExist = ipAttrRes.data && ipAttrRes.data.length > 0;
        }))
        .finally(() => { this.loaded = true; });
    },
    getSelectedPageSize(selected) {
      this.currentPage = 1;
      this.perPage = selected;
    },
    formatDate(dateStr) {
      return moment(dateStr).format(momentTimeFormat.dateYearTime);
    },
    showJsonModal(rowData) {
      this.selectedJson = JSON.stringify(rowData, null, 4);
      this.$bvModal.show('json-modal');
    },
    showXtendTraffic(rowData) {
      this.flowRow = rowData;
    },
  },
};
</script>
<style>
.header-card {
  width: 100%;
}
.header-card>.card-body {
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
}
.details-row {
  display: flex;
  flex-wrap: wrap;
  column-gap: 5rem;
}
.details-row span {
  white-space: nowrap;
}
</style>
